import { z } from 'zod';
/**
 * Esquemas de catalogo. Fuente unica: los usan la API, la interfaz y la
 * importacion CSV. Convenciones de Enteratek, seccion 6.
 */
/**
 * Clave `pre_snake_case`, ASCII minuscula, maximo 24, inmutable una vez usada.
 *
 * El prefijo se VALIDA antes de entrar al patron, por dos razones concretas:
 *
 * 1. Interpolar texto en un `RegExp` sin escapar es como se ensancha una
 *    validacion sin querer. Hoy todos los prefijos son literales del codigo
 *    —`cli`, `art`, `paq`— asi que no hay por donde entrar; pero esta funcion
 *    se exporta y recibe un `string`, y el dia que un prefijo venga de
 *    configuracion o de una fila de catalogo, un `.*` adentro acepta
 *    cualquier cosa. Mas facil impedirlo ahora que auditarlo luego.
 *
 * 2. Un prefijo de 23 caracteres o mas produce el cuantificador `{1,-2}`, que
 *    JavaScript no rechaza: lo trata como texto literal, y entonces el patron
 *    deja de aceptar TODA clave de ese catalogo. Comprobado: con el prefijo
 *    de 25 caracteres, `prefijo_a` no pasa. Falla silencioso y en el lugar
 *    equivocado — la queja llega como "no puedo dar de alta nada".
 */
const PREFIJO_VALIDO = /^[a-z]{2,12}$/;
export const claveCatalogo = (prefijo) => {
    if (!PREFIJO_VALIDO.test(prefijo)) {
        /* Se lanza al CONSTRUIR el esquema, no al validar un dato: es un error de
           programacion y sale al arrancar, no en una peticion de un usuario. */
        throw new Error(`Prefijo de catalogo invalido: "${prefijo}". Dos a doce letras minusculas, sin nada mas.`);
    }
    return z
        .string()
        .trim()
        .toLowerCase()
        .regex(
    /* Excepcion revisada. `prefijo` acaba de pasar por PREFIJO_VALIDO, que
       solo admite dos a doce letras minusculas: no hay metacaracter posible
       ni cuantificador negativo. La regla se queda encendida a proposito,
       para que el proximo `new RegExp` interpolado tenga que justificarse. */
    /* La supresion es sin nombre de regla a proposito: aplica a esta linea
       y nada mas, y esta linea esta revisada por completo arriba. Nombrar
       las reglas una por una obliga a editar el comentario cada vez que se
       agrega un analizador que mira lo mismo. */
    // nosemgrep
    new RegExp(`^${prefijo}_[a-z0-9_]{1,${23 - prefijo.length}}$`), `La clave debe empezar con ${prefijo}_ y usar solo minusculas, numeros y guion bajo`);
};
export const nombreCatalogo = z.string().trim().min(2).max(80);
/** Alta generica de catalogo simple: clave, nombre y activo. */
export const catalogoSimple = (prefijo) => z.object({
    clave: claveCatalogo(prefijo),
    nombre: nombreCatalogo,
    activo: z.boolean().optional(),
});
/**
 * Edicion de catalogo: la clave NO esta. Es inmutable una vez usada, y un
 * esquema que la acepta invita a mandarla; mejor que no exista el campo.
 */
export const catalogoSimpleEdicion = z.object({
    nombre: nombreCatalogo.optional(),
    activo: z.boolean().optional(),
});
/* ------------------------------ Ciudades -------------------------------- */
export const horaReloj = z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/);
/* La forma base se declara aparte para poder derivar la edicion de ella:
   un objeto con .refine() ya no es un ZodObject y pierde .partial(). */
const ciudadCampos = z.object({
    clave: claveCatalogo('ciu'),
    nombre: nombreCatalogo,
    latitud: z.number().min(-90).max(90).optional(),
    longitud: z.number().min(-180).max(180).optional(),
    ventana_inicio: horaReloj.optional(),
    ventana_fin: horaReloj.optional(),
    activo: z.boolean().optional(),
});
const coherenciaCiudad = [
    {
        regla: (c) => (c.latitud === undefined) === (c.longitud === undefined),
        mensaje: 'Latitud y longitud van juntas o no van',
    },
    {
        regla: (c) => c.ventana_inicio === undefined ||
            c.ventana_fin === undefined ||
            c.ventana_inicio < c.ventana_fin,
        mensaje: 'La ventana operativa termina despues de empezar',
    },
];
export const ciudadAlta = coherenciaCiudad.reduce((esquema, { regla, mensaje }) => esquema.refine(regla, { message: mensaje }), ciudadCampos);
export const ciudadEdicion = coherenciaCiudad.reduce((esquema, { regla, mensaje }) => esquema.refine(regla, { message: mensaje }), ciudadCampos.partial().omit({ clave: true }));
/* ------------------------------ Causales -------------------------------- */
export const IMPUTABLES = ['CLIENTE', 'IDC', 'CARRIER', 'ENTERATEK'];
export const causalAlta = z.object({
    clave: claveCatalogo('cau'),
    nombre: nombreCatalogo,
    /* A quien se imputa y si consume saldo son reglas de negocio con
       consecuencia economica, no adorno del catalogo. */
    imputable_a: z.enum(IMPUTABLES),
    consume_saldo: z.boolean(),
    activo: z.boolean().optional(),
});
export const causalEdicion = causalAlta.partial().omit({ clave: true });
/* ----------------------------- Estados ---------------------------------- */
export const estadoAlta = z.object({
    clave: claveCatalogo('edo'),
    nombre: nombreCatalogo,
    abreviatura: z.string().trim().min(2).max(5),
    activo: z.boolean().optional(),
});
export const estadoEdicion = estadoAlta.partial().omit({ clave: true });
/* ---------------------------- Paqueterias -------------------------------- */
export const paqueteriaAlta = z.object({
    clave: claveCatalogo('paq'),
    nombre: nombreCatalogo,
    prefijo: z.string().trim().min(2).max(4).optional(),
    /* https obligatorio, sin credenciales embebidas, y con el marcador {guia}:
       sin el marcador la liga de rastreo no lleva a ningun lado. */
    url_rastreo: z
        .string()
        .url()
        .regex(/^https:\/\/[^@\s]+$/, 'Debe ser https y sin credenciales embebidas')
        .regex(/\{guia\}/, 'Falta el marcador {guia} en la plantilla')
        .optional(),
    activo: z.boolean().optional(),
});
export const paqueteriaEdicion = paqueteriaAlta.partial().omit({ clave: true });
//# sourceMappingURL=catalogos.js.map